import React, { useState, useEffect } from 'react';
import { Plug, CheckCircle2, AlertCircle, ExternalLink, ChevronRight, Lock, Sparkles, Mail, CreditCard, MessageSquare, Globe, FileSpreadsheet } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { openSettingsTab } from '../lib/onboardingSteps';

export default function Integraties({ onNavigate }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const load = () => carsysApi.getIntegrationsOverview().then(setData).catch(() => setData(null));
  useEffect(() => { load(); }, []);

  const st = data?.status || {};
  const licensed = !!data?.licensed;
  const skipped = data?.skipped || [];
  const skippable = data?.skippable || [];
  const toggleSkip = async (key, skip) => {
    try { await carsysApi.skipIntegration(key, skip); } catch (_) { /* noop */ }
    load();
  };

  const rows = [
    {
      key: 'gemini', icon: Sparkles, naam: 'Google Gemini', pro: false,
      doel: t('AI-diagnose, bonnen scannen (OCR), samenvattingen', 'AI-diagnose, bonnen scannen (OCR), samenvattingen'),
      nodig: t('Gratis API-sleutel', 'Gratis API-sleutel'),
      link: { url: 'https://aistudio.google.com/apikey', label: 'aistudio.google.com/apikey' },
      go: () => openSettingsTab(onNavigate, 'licentie'),
    },
    {
      key: 'email', icon: Mail, naam: t('E-mail (SMTP)', 'E-mail (SMTP)'), pro: false,
      doel: t('Facturen & herinneringen vanuit je eigen e-mail', 'Facturen & herinneringen vanuit je eigen e-mail'),
      nodig: t('SMTP-gebruiker & wachtwoord (bijv. Gmail-app-wachtwoord)', 'SMTP-gebruiker & wachtwoord (bijv. Gmail-app-wachtwoord)'),
      link: { url: 'https://support.google.com/mail/answer/185833', label: 'Gmail app-wachtwoord' },
      go: () => openSettingsTab(onNavigate, 'smtp'),
    },
    {
      key: 'stripe', icon: CreditCard, naam: 'Stripe', pro: false,
      doel: t('Online betalen (iDEAL/creditcard) via betaallink', 'Online betalen (iDEAL/creditcard) via betaallink'),
      nodig: t('Secret key (sk_live_… of sk_test_…)', 'Secret key (sk_live_… of sk_test_…)'),
      link: { url: 'https://dashboard.stripe.com/apikeys', label: 'dashboard.stripe.com' },
      go: () => openSettingsTab(onNavigate, 'betalen'),
    },
    {
      key: 'twilio', icon: MessageSquare, naam: 'Twilio', pro: true,
      doel: t('SMS/WhatsApp-afspraakherinneringen', 'SMS/WhatsApp-afspraakherinneringen'),
      nodig: t('Account SID + Auth Token + afzendernummer', 'Account SID + Auth Token + afzendernummer'),
      link: { url: 'https://www.twilio.com/console', label: 'twilio.com/console' },
      go: () => onNavigate('smsherinneringen'),
    },
    {
      key: 'storecove', icon: Globe, naam: t('Storecove (Peppol)', 'Storecove (Peppol)'), pro: true,
      doel: t('E-facturen écht versturen over het Peppol-netwerk', 'E-facturen écht versturen over het Peppol-netwerk'),
      nodig: t('API-sleutel (betaald abonnement)', 'API-sleutel (betaald abonnement)'),
      link: { url: 'https://www.storecove.com', label: 'storecove.com' },
      go: () => onNavigate('efacturatie'),
    },
    {
      key: 'digipoort', icon: FileSpreadsheet, naam: t('BTW-aangifte (Digipoort)', 'BTW-aangifte (Digipoort)'), pro: true,
      doel: t('XBRL-aangifte genereren; échte indiening vereist PKIoverheid-certificaat', 'XBRL-aangifte genereren; échte indiening vereist PKIoverheid-certificaat'),
      nodig: t('Geen sleutel — XBRL-export beschikbaar; certificaat alleen voor automatische indiening', 'Geen sleutel — XBRL-export beschikbaar; certificaat alleen voor automatische indiening'),
      link: { url: 'https://www.logius.nl/diensten/digipoort', label: 'logius.nl/digipoort' },
      go: () => onNavigate('boekhouding'),
      info: true,
    },
  ];

  return (
    <div className="cs-page" data-testid="integraties-page" style={{ padding: 24, maxWidth: 1000 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Plug size={22} color="#2563eb" />
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a' }}>{t('Integraties & accounts', 'Integraties & accounts')}</h1>
      </div>
      <p style={{ color: '#64748b', margin: '0 0 18px', fontSize: 14 }}>
        {t('Overzicht van externe diensten en welke account/sleutel je waar nodig hebt.', 'Overzicht van externe diensten en welke account/sleutel je waar nodig hebt.')}
      </p>

      <div style={{ display: 'grid', gap: 12 }}>
        {rows.map(r => {
          const Icon = r.icon;
          const locked = r.pro && !licensed;
          const isSkipped = skipped.includes(r.key);
          const isSkippable = skippable.includes(r.key);
          const configured = !!st[r.key] && !isSkipped;
          const done = !!st[r.key];
          return (
            <div key={r.key} data-testid={`integration-${r.key}`} style={{ ...card, opacity: locked ? 0.8 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
                <div style={iconWrap}><Icon size={18} color="#2563eb" /></div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 15.5, color: '#0f172a' }}>{r.naam}</span>
                    {r.pro && <span style={proBadge}>PRO</span>}
                    {r.info ? <span style={infoBadge}>{t('Info', 'Info')}</span>
                      : isSkipped ? <span style={offBadge} data-testid={`integration-status-${r.key}-off`}>{t('Uitgeschakeld', 'Uitgeschakeld')}</span>
                      : (configured ? <span style={okBadge} data-testid={`integration-status-${r.key}-ok`}><CheckCircle2 size={12} /> {t('Ingesteld', 'Ingesteld')}</span>
                        : <span style={todoBadge} data-testid={`integration-status-${r.key}-todo`}><AlertCircle size={12} /> {t('Nog instellen', 'Nog instellen')}</span>)}
                  </div>
                  <div style={{ fontSize: 13, color: '#475569', margin: '6px 0 4px' }}>{r.doel}</div>
                  <div style={{ fontSize: 12.5, color: '#64748b' }}><b>{t('Nodig:', 'Nodig:')}</b> {r.nodig}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
                  {locked ? (
                    <button style={primaryBtn} data-testid={`integration-upgrade-${r.key}`} onClick={() => openSettingsTab(onNavigate, 'plannen')}><Lock size={13} /> {t('PRO', 'PRO')}</button>
                  ) : (
                    <button style={primaryBtn} data-testid={`integration-go-${r.key}`} onClick={r.go}>{t('Openen', 'Openen')} <ChevronRight size={14} /></button>
                  )}
                  {!locked && isSkippable && !configured && (
                    isSkipped ? (
                      <button style={undoLink} data-testid={`integration-enable-${r.key}`} onClick={() => toggleSkip(r.key, false)}>{t('Toch gebruiken', 'Toch gebruiken')}</button>
                    ) : (
                      <button style={skipLink} data-testid={`integration-skip-${r.key}`} onClick={() => toggleSkip(r.key, true)}>{t('Niet gebruiken', 'Niet gebruiken')}</button>
                    )
                  )}
                  {r.link && <a href={r.link.url} target="_blank" rel="noreferrer" style={extLink} data-testid={`integration-link-${r.key}`}>{r.link.label} <ExternalLink size={11} /></a>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, boxShadow: '0 2px 6px rgba(15,23,42,.04)' };
const iconWrap = { width: 40, height: 40, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 14px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' };
const extLink = { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2563eb', fontWeight: 600, fontSize: 12, textDecoration: 'none' };
const proBadge = { fontSize: 10, fontWeight: 800, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 99 };
const okBadge = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, background: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: 99 };
const todoBadge = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, background: '#fef2f2', color: '#b91c1c', padding: '2px 8px', borderRadius: 99 };
const infoBadge = { fontSize: 11, fontWeight: 700, background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 99 };
const offBadge = { fontSize: 11, fontWeight: 700, background: '#f1f5f9', color: '#475569', padding: '2px 8px', borderRadius: 99 };
const skipLink = { background: 'transparent', border: 'none', color: '#94a3b8', fontWeight: 600, fontSize: 12, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' };
const undoLink = { background: 'transparent', border: 'none', color: '#2563eb', fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: 0, whiteSpace: 'nowrap' };
